import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ChangeDetectionStrategy,
  NgZone,
  ViewChild,
} from "@angular/core";
import { default as vegaEmbed } from "vega-embed";

import { chartsData } from "./charts-data.interface";
import { ChartsService } from "./charts.service";
import { HsLanguageService } from "hslayers-ng";

@Component({
  selector: "ac-charts",
  templateUrl: "./charts.component.html",
  styleUrls: ["./charts.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartsComponent implements OnChanges {
  @Input() data;
  @ViewChild("chartContainer") chartContainer: ElementRef;
  chartWidth;
  chartHeight = 0.13;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data.currentValue) {
      setTimeout(() => {
        this.renderCharts(changes.data.currentValue);
      }, 100);
    }
  }

  constructor(
    public hsLangService: HsLanguageService,
    public elementRef: ElementRef,
    public ChartsService: ChartsService,
    private zone: NgZone
  ) {}

  renderCharts(data: chartsData): void {
    let width =
      document.querySelector(data.chartContainerClass).clientWidth * 0.9;
    width = width === 0 ? 450 : width / (data.columns + 0.2);

    this.chartWidth = width;
    this.chartHeight = data.chartHeight ?? 0.2285;
    const chartData = {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      config: {
        mark: {
          tooltip: null,
          font: "Roboto",
        },
        axis: {
          labelFont: "Roboto",
          titleFont: "Roboto",
          titleFontSize: 13,
          labelFontSize: 12,
        },
        legend: { labelFont: "Roboto", titleFont: "Roboto" },
        header: { labelFont: "Roboto", titleFont: "Roboto" },
        title: { font: "Roboto", subtitleFont: "Roboto", fontSize: 16 },
      },
      data: {
        name: "week_data",
      },
      datasets: data.datasets,
      columns: 1,
      hconcat: [],
    };

    const concat = { concat: [], columns: data.columns };

    // Create json spec for each individual chart
    for (const chart of data.definitions.charts[data.dataFrequency]) {
      // TODO: Get rid of this unecessary step
      const options = {
        yAxis: chart.data,
        mark: chart.mark,
        uncertainity: this.hasUncertainity(chart.data, data.uncertainity),
        dataFrequency: data.dataFrequency,
        overridesFunction: data.componentChartOverrides,
        containerClass: data.chartContainerClass,
        //chart.title can be present if its layered chart (multiple marks,factors)
        chartTitle:
          chart.title ??
          (Array.isArray(chart.data) ? chart.data : chart.data[0]),
        layered: chart.layered,
        unit: chart.unit,
        overview: data.overview,
      };
      if (chart.mark === "rect") {
        data.definitions.rect.width = this.chartWidth;
        chartData.hconcat.push(data.definitions.rect);
      } else {
        concat.concat.push(this.generateChartJSON(options));
      }
    }

    // 2 layers of charts
    // hconcat [rect,rest]
    // concat = rest [defitinion charts]
    chartData.hconcat.push(concat);
    chartData.hconcat[0].resolve = { scale: { color: "independent" } };
    // Render vega view
    try {
      this.zone.runOutsideAngular(() => {
        vegaEmbed(this.chartContainer.nativeElement, chartData, {
          renderer: "svg",
        }).then((res) => {
          // this.ChartsService.generatingCharts = false;
          // View event listener - possibliy to make configurable
          res.view.addEventListener("dblclick", (event, item) => {
            if (item) {
              this.ChartsService.chartClicked.next({ event, item, res });
            }
          });
          this.ChartsService.chartsGenerated.next({ res, data });
        });
      });
    } catch (ex) {
      console.warn("Could not create vega chart:", ex);
    }
  }

  /**
   * Decides whether the chart should or should not be rendered with uncertainity bars
   */
  hasUncertainity(factor: string[] | string, uncertainity) {
    if (Array.isArray(factor)) return uncertainity;
    return ["fertdate"].includes(factor as any as string)
      ? uncertainity
      : uncertainity;
  }

  /**
   * Creates vega spec object. yAxis is expected as string || string[]
   * String - only one chart layer defined
   * String [] - multiple layers for single chart defined || group of individual chats
   * @example definitions: {
        charts: {
          monthly: [
            {
              data: ['preci','temp],
              mark: 'bar'
            }
          ]
        }
   */
  generateChartJSON(options: any): any {
    const timeUnit = options.dataFrequency;
    let chartTitle;
    const chartTemplate = {
      layer: [],
      name: "",
    };

    //TODO: UNCERTAINY FOR LAYERED CHARTS M
    if (Array.isArray(options.yAxis)) {
      for (const [index, subChartYAxis] of options.yAxis.entries()) {
        chartTemplate.name = `agroInfoChart-${
          options.layered ? options.yAxis : subChartYAxis
        }`;
        chartTitle = options.chartTitle ?? subChartYAxis;
        chartTemplate.layer.push(
          this.generateDefaultChartLayer(
            options,
            timeUnit,
            chartTitle,
            index,
            options.layered
          )
        );
        if (options.dataFrequency == "week") {
          chartTemplate.layer.unshift(this.generateFrostPeriodLayer(options));
          chartTemplate["resolve"] = { scale: { color: "independent" } };
        }
        if (options.uncertainity) {
          chartTemplate.layer.push(
            this.generateUncertainityBar(
              timeUnit,
              options.yAxis[index],
              options
            )
          );
        }
      }
    } else {
      chartTemplate.name = `agroInfoChart-${options.yAxis}`;
      chartTitle = options.yAxis;
      chartTemplate.layer.push(
        this.generateDefaultChartLayer(options, timeUnit, chartTitle)
      );
      if (options.dataFrequency == "week") {
        chartTemplate.layer.unshift(this.generateFrostPeriodLayer(options));
        chartTemplate["resolve"] = { scale: { color: "independent" } };
      }
      if (options.uncertainity) {
        chartTemplate.layer.push(
          this.generateUncertainityBar(timeUnit, options.yAxis, options)
        );
      }
      // Add regresion line to layered chart
      if (
        options.dataFrequency == "year" &&
        !(options.yAxis == "gdu" && options.mark == "bar")
      ) {
        chartTemplate.layer.push(this.addRegresionLine(options, timeUnit));
      }
    }

    // Finally make chart specific changes via overridesFunction
    if (options.overridesFunction) {
      options.overridesFunction(
        options,
        chartTemplate,
        {
          width: this.chartWidth,
          height:
            document.querySelector(options.containerClass).clientWidth *
            this.chartHeight,
        },
        this.hsLangService
      );
    }

    return chartTemplate;
  }

  /**
   * @returns Translated string with corresponding data frequency used as axis title
   */
  getChartXAxisTitle(dataFrequency): string {
    return this.hsLangService.getTranslationIgnoreNonExisting(
      "STRATEGY",
      dataFrequency
    );
  }

  generateFrostPeriodLayer(options) {
    const frostPeriodChart = {
      width: this.chartWidth,
      height:
        document.querySelector(options.containerClass).clientWidth *
        this.chartHeight,
      mark: { type: "rect" },
      encoding: {
        x: { field: "week", timeUnit: "yearweek", type: "ordinal" },
        color: {
          field: "frost",
          legend: { labelLimit: 50, title: null },
          scale: {
            range: ["grey"],
          },
        },
        opacity: { value: 0.2 },
        tooltip: [{ field: "frost", title: "." }],
      },
      transform: [{ filter: "datum.frost" }],
    };
    if (options.mark == "bar") {
      frostPeriodChart.mark["width"] = { band: 0.9 };
      frostPeriodChart.encoding["stroke"] = { value: "grey" };
    }
    return frostPeriodChart;
  }

  /**
   * Creates additional uncertainity layer that will be added to the chart
   * @returns Vega chart layer
   */
  generateUncertainityBar(timeUnit, yAxis, options): any {
    if (yAxis === "fertdate")
      return {
        width: this.chartWidth,
        height:
          document.querySelector(options.containerClass).clientWidth *
          this.chartHeight,
      };
    const axis =
      options.dataFrequency == "week"
        ? { labelAngle: 0 }
        : { ticks: false, labels: false };
    const layer = {
      width: this.chartWidth,
      height:
        document.querySelector(options.containerClass).clientWidth *
        this.chartHeight,
      mark: {
        type: "errorbar",
        ticks: { color: "red" },
        color: "red",
        // extent: "ci",
        // borders: true,
        // color: '#C4C4C4',
        // opacity: 0.5
      },
      encoding: {
        x: {
          field: timeUnit,
          timeUnit: timeUnit != "year" ? "year" + timeUnit : timeUnit, //yearweek fo,
          axis: axis,
          type: "ordinal",
          title: false,
        },
        // "yError": { "field": `${yAxis}unc`,"type": "quantitative", },
        y: {
          axis: { title: "" },
          field: `${yAxis}up`,
          type: "quantitative",
          scale: { zero: false },
        },
        y2: {
          field: `${yAxis}dwn`,
          axis: null,
        },
      },
    };

    return layer;
  }

  /**
   * Creates default chart layer definition vased on provided options
   * @returns Vega chart layer
   * @param namedAxes boolean - optional parameter used for layered charts. If true y axis are named with translated factor name
   */
  generateDefaultChartLayer(options, timeUnit, chartTitle, index?, namedAxes?) {
    const yAxis =
      typeof index == "number" ? options.yAxis[index] : options.yAxis;
    const yAxisTitle = namedAxes
      ? this.hsLangService.getTranslationIgnoreNonExisting(
          "CHARTS.title",
          options.yAxis[index]
        ) +
        ` [${this.hsLangService.getTranslationIgnoreNonExisting(
          "CHARTS.title",
          options.unit[index]
        )}]`
      : `${this.hsLangService.getTranslationIgnoreNonExisting(
          "CHARTS.title",
          options.unit
        )}`;
    return <any>{
      title: {
        text: this.hsLangService.getTranslationIgnoreNonExisting(
          "CHARTS.title",
          chartTitle
        ),
      },
      width: this.chartWidth,
      height:
        document.querySelector(options.containerClass).clientWidth *
        this.chartHeight,
      selection: {
        my_selection: {
          type: "interval",
          empty: "none",
          mark: { stroke: "grey" },
          encodings: ["x"],
        },
      },
      encoding: {
        x: {
          axis: {
            title: this.getChartXAxisTitle(options.dataFrequency),
            titleFontStyle: "bold",
            //https://github.com/d3/d3-time-format#locale_format
            format: options.dataFrequency == "week" ? "%-W" : "%Y",
            formatType: "time",
          },
          field: timeUnit,
          timeUnit: timeUnit != "year" ? "year" + timeUnit : timeUnit, //yearweek fo
          type: "ordinal",
        },

        y: {
          axis: {
            title: yAxisTitle,
            titleFontSize: 14,
          },
          field: options.mark === "rect" ? "week" : yAxis,
          type: "quantitative",
          scale: {
            zero: false,
          },
          // Makes BARs to not start with 0
          stack: "none",
        },
        color: {
          condition: {
            selection: "my_selection",
            value: "#007bff",
          },
          value: "gray",
        },
        tooltip: [
          {
            field: timeUnit,
            scale: { type: "utc" },
            title: this.getChartXAxisTitle(options.dataFrequency),
            axis: { format: options.dataFrequency == "week" ? "%-W" : "%Y" },
          },
          { field: yAxis, type: "quantitative", title: yAxisTitle },
        ],
      },
      mark: {
        type: typeof index == "number" ? options.mark[index] : options.mark,
        tooltip: true,
      },
    };
  }
  /**
   * Creates regresion line chart layer
   * @returns Vega chart layer
   */
  addRegresionLine(options, timeUnit) {
    return <any>{
      width: this.chartWidth,
      height:
        document.querySelector(options.containerClass).clientWidth *
        this.chartHeight,
      mark: {
        type: "line",
        color: "#fcba03",
      },
      transform: [
        {
          regression: options.yAxis,
          on: timeUnit,
        },
      ],
      encoding: {
        x: {
          field: timeUnit,
          type: "quantitative",
          axis: null,
        },
        y: {
          field: options.yAxis,
          type: "quantitative",
        },
      },
    };
  }
}
